import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronRight, Database, Loader2, X } from "lucide-react";
import type { CuadraChatClient } from "../../lib/cuadraChatClient";
import { createOriginalFormatFile } from "../../utils/artifactOriginalFile";
import { isZipArchiveFile } from "../../utils/isZipArchiveFile";
import { ARTIFACT_TYPE_EXTENSIONS, type ArtifactType } from "../../types/artifact";

interface ArtifactSnapshot {
  id: string;
  title: string;
  type: string;
  content: string;
  chatId?: string;
}

interface Dataset {
  id: string;
  name: string;
  description?: string | null;
}

type ModalStep = "select" | "uploading" | "success" | "error";

interface SaveToKnowledgeModalProps {
  artifact: ArtifactSnapshot;
  client: CuadraChatClient;
  onClose: () => void;
}

export const SaveToKnowledgeModal: FC<SaveToKnowledgeModalProps> = ({
  artifact,
  client,
  onClose,
}) => {
  const [step, setStep] = useState<ModalStep>("select");
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(true);
  const [datasetsError, setDatasetsError] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const ext =
    ARTIFACT_TYPE_EXTENSIONS[artifact.type as ArtifactType] ?? ".txt";

  useEffect(() => {
    let cancelled = false;
    setIsLoadingDatasets(true);
    setDatasetsError(null);
    client
      .listDatasets()
      .then((data) => {
        if (!cancelled) {
          setDatasets(data);
          setIsLoadingDatasets(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDatasetsError(
            err instanceof Error ? err.message : "Failed to load datasets",
          );
          setIsLoadingDatasets(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleSave = useCallback(
    async (datasetId: string) => {
      setSelectedDatasetId(datasetId);
      setUploadError(null);
      setStep("uploading");
      try {
        const file = createOriginalFormatFile(
          artifact.content,
          artifact.title,
          artifact.type,
        );
        if (isZipArchiveFile(file)) {
          const { files, skipped } = await client.expandZipFile(
            file,
            "dataset",
            datasetId,
          );
          if (files.length === 0) {
            const msg =
              skipped.length > 0
                ? skipped.map((s) => `${s.filename}: ${s.reason}`).join("; ")
                : "No files could be extracted from the archive.";
            throw new Error(msg);
          }
        } else {
          await client.uploadFile(file, "dataset", datasetId);
        }
        setStep("success");
      } catch (err: unknown) {
        setUploadError(
          err instanceof Error ? err.message : "Upload failed. Please try again.",
        );
        setStep("error");
      }
    },
    [artifact, client],
  );

  const selectedDatasetName =
    datasets.find((d) => d.id === selectedDatasetId)?.name ?? "your dataset";

  return (
    <div
      className="cuadra-fixed cuadra-inset-0 cuadra-z-[9999] cuadra-flex cuadra-items-center cuadra-justify-center cuadra-p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cuadra-absolute cuadra-inset-0 cuadra-bg-black/50" aria-hidden="true" />

      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Save to Knowledge"
        className="cuadra-relative cuadra-bg-background cuadra-border cuadra-border-border cuadra-rounded-2xl cuadra-shadow-2xl cuadra-w-full cuadra-max-w-sm cuadra-flex cuadra-flex-col cuadra-overflow-hidden"
      >
        <div className="cuadra-flex cuadra-items-center cuadra-justify-between cuadra-px-5 cuadra-py-4 cuadra-border-b cuadra-border-border">
          <div className="cuadra-flex cuadra-items-center cuadra-gap-2.5">
            <Database
              size={15}
              className="cuadra-flex-shrink-0 cuadra-text-muted-foreground"
            />
            <span className="cuadra-text-sm cuadra-font-medium cuadra-text-foreground">
              Save to Knowledge
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="cuadra-p-1.5 cuadra-rounded-lg cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="cuadra-overflow-y-auto">
          {step === "select" && (
            <>
              <div className="cuadra-px-5 cuadra-pt-4 cuadra-pb-3 cuadra-space-y-3">
                <p className="cuadra-text-xs cuadra-text-muted-foreground cuadra-leading-relaxed">
                  Saves{" "}
                  <strong className="cuadra-font-medium cuadra-text-foreground">
                    {artifact.title || "this artifact"}
                  </strong>{" "}
                  in the original format{" "}
                  <span className="cuadra-font-medium cuadra-text-foreground">
                    ({ext})
                  </span>
                  . Choose a dataset below.
                </p>
              </div>

              {isLoadingDatasets && (
                <div className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-py-10">
                  <Loader2
                    size={22}
                    className="cuadra-animate-spin cuadra-text-muted-foreground"
                  />
                </div>
              )}

              {!isLoadingDatasets && datasetsError && (
                <p className="cuadra-px-5 cuadra-pb-5 cuadra-text-sm cuadra-text-destructive">
                  {datasetsError}
                </p>
              )}

              {!isLoadingDatasets && !datasetsError && datasets.length === 0 && (
                <p className="cuadra-px-5 cuadra-pb-5 cuadra-text-sm cuadra-text-muted-foreground">
                  No datasets found. Create a dataset in the dashboard first.
                </p>
              )}

              {!isLoadingDatasets && !datasetsError && datasets.length > 0 && (
                <ul className="cuadra-px-3 cuadra-pb-3 cuadra-space-y-1.5">
                  {datasets.map((ds) => (
                    <li key={ds.id}>
                      <button
                        type="button"
                        onClick={() => void handleSave(ds.id)}
                        className="cuadra-w-full cuadra-flex cuadra-items-center cuadra-gap-3 cuadra-px-3.5 cuadra-py-3 cuadra-rounded-xl cuadra-border cuadra-border-border hover:cuadra-border-primary/40 hover:cuadra-bg-primary/5 cuadra-transition-all cuadra-text-left cuadra-group"
                      >
                        <div className="cuadra-flex-shrink-0 cuadra-p-1.5 cuadra-rounded-lg cuadra-bg-muted cuadra-text-muted-foreground group-hover:cuadra-bg-primary/10 group-hover:cuadra-text-primary cuadra-transition-colors">
                          <Database size={13} />
                        </div>
                        <div className="cuadra-flex-1 cuadra-min-w-0">
                          <p className="cuadra-text-sm cuadra-font-medium cuadra-text-foreground cuadra-truncate">
                            {ds.name}
                          </p>
                          {ds.description && (
                            <p className="cuadra-text-xs cuadra-text-muted-foreground cuadra-truncate">
                              {ds.description}
                            </p>
                          )}
                        </div>
                        <ChevronRight
                          size={14}
                          className="cuadra-flex-shrink-0 cuadra-text-muted-foreground/40 group-hover:cuadra-text-primary cuadra-transition-colors"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {step === "uploading" && (
            <div className="cuadra-flex cuadra-flex-col cuadra-items-center cuadra-justify-center cuadra-py-14 cuadra-gap-3">
              <Loader2
                size={24}
                className="cuadra-animate-spin cuadra-text-primary"
              />
              <p className="cuadra-text-sm cuadra-text-muted-foreground">
                Saving to dataset…
              </p>
            </div>
          )}

          {step === "success" && (
            <div className="cuadra-flex cuadra-flex-col cuadra-items-center cuadra-justify-center cuadra-py-14 cuadra-gap-3 cuadra-px-5 cuadra-text-center">
              <div className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-w-10 cuadra-h-10 cuadra-rounded-full cuadra-bg-green-500/10">
                <Check size={18} className="cuadra-text-green-500" />
              </div>
              <p className="cuadra-text-sm cuadra-font-medium cuadra-text-foreground">
                Saved successfully
              </p>
              <p className="cuadra-text-xs cuadra-text-muted-foreground cuadra-leading-relaxed">
                Uploaded{" "}
                <strong className="cuadra-font-medium cuadra-text-foreground">
                  {artifact.title || "artifact"}
                  {ext}
                </strong>{" "}
                to{" "}
                <strong className="cuadra-font-medium cuadra-text-foreground">
                  {selectedDatasetName}
                </strong>
                .
              </p>
              <button
                type="button"
                onClick={onClose}
                className="cuadra-mt-2 cuadra-px-5 cuadra-py-1.5 cuadra-rounded-lg cuadra-text-sm cuadra-font-medium cuadra-bg-primary cuadra-text-primary-foreground hover:cuadra-opacity-90 cuadra-transition-opacity"
              >
                Done
              </button>
            </div>
          )}

          {step === "error" && (
            <div className="cuadra-flex cuadra-flex-col cuadra-items-center cuadra-justify-center cuadra-py-14 cuadra-gap-3 cuadra-px-5 cuadra-text-center">
              <p className="cuadra-text-sm cuadra-text-destructive">
                {uploadError ?? "Upload failed"}
              </p>
              <button
                type="button"
                onClick={() => setStep("select")}
                className="cuadra-px-4 cuadra-py-1.5 cuadra-rounded-lg cuadra-text-sm cuadra-border cuadra-border-border hover:cuadra-bg-muted cuadra-transition-colors"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
